import {
  Bell,
  Building2,
  CreditCard,
  Landmark,
  LayoutDashboard,
  Map,
  Package,
  Radio,
  Route,
  Waypoints,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { ComponentType } from 'react';

import type { Messages } from '@core/i18n';
import type { UserRole } from '@core/types/api';

export interface NavItem {
  to: string;
  /** Resolved against the message catalogue, so labels translate with the locale. */
  label: (t: Messages) => string;
  icon: ComponentType<{ className?: string }>;
  /** Minimum role to SEE the item. Hierarchical. */
  role?: UserRole;
  /** Exact match only -- without it "/" would highlight on every route. */
  end?: boolean;
}

export interface NavSection {
  id: string;
  title: (t: Messages) => string;
  items: NavItem[];
  /**
   * What the section needs to exist at all. `organization` sections are tenant screens and
   * vanish for a platform admin outside every organization -- each would only answer 403
   * NO_ORGANIZATION. `platform` sections are for platform administrators only.
   */
  scope: 'organization' | 'platform';
}

/**
 * Grouped by what the user is doing, not by data model.
 *
 * "Operate" is the day-to-day loop -- where is everything, what needs attention.
 * "Fleet" is the inventory behind it, touched weekly rather than hourly.
 * "Organization" is administration, touched rarely and mostly by one person.
 * "Platform" runs the whole service, and only platform administrators see it.
 *
 * Ordering follows frequency of use, so the things a dispatcher opens all day sit
 * closest to the top of the pointer's travel.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'operate',
    title: (t) => t.nav.operate,
    scope: 'organization',
    items: [
      { to: '/', label: (t) => t.nav.fleetMap, icon: Map, end: true },
      { to: '/dashboard', label: (t) => t.nav.dashboard, icon: LayoutDashboard },
      { to: '/alerts', label: (t) => t.nav.alerts, icon: Bell },
      { to: '/trips', label: (t) => t.nav.trips, icon: Route },
    ],
  },
  {
    id: 'fleet',
    title: (t) => t.nav.library,
    scope: 'organization',
    items: [
      { to: '/trackers', label: (t) => t.nav.trackers, icon: Radio },
      { to: '/assets', label: (t) => t.nav.assets, icon: Package },
      // Waypoints, not Route: lucide's Route icon is already /trips above, and two nav
      // items sharing a glyph in a twelve-item rail is a navigation error, not a nit.
      { to: '/routes', label: (t) => t.nav.routes, icon: Waypoints },
      { to: '/geofences', label: (t) => t.nav.geofences, icon: Building2 },
    ],
  },
  {
    id: 'organization',
    title: (t) => t.nav.organization,
    scope: 'organization',
    items: [
      { to: '/users', label: (t) => t.nav.users, icon: Users, role: 'ADMIN' },
      { to: '/settings', label: (t) => t.nav.settings, icon: Settings },
    ],
  },
  {
    // Last, because it is the least frequent and the most powerful: it reaches across
    // every tenant, so it should never be the thing a slipped click lands on.
    id: 'platform',
    title: (t) => t.nav.platform,
    scope: 'platform',
    items: [
      // Landmark, not Building2: Building2 is already the geofence glyph above.
      { to: '/platform/organizations', label: (t) => t.nav.platformOrganizations, icon: Landmark },
      { to: '/platform/plans', label: (t) => t.nav.platformPlans, icon: CreditCard },
      { to: '/platform/admins', label: (t) => t.nav.platformAdmins, icon: ShieldCheck },
    ],
  },
];
